import React, { useState, useEffect, useRef, } from "react";
import { useNavigate } from "react-router-dom";
import { Link as Link2 } from "react-router-dom";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import not_found from "../../assets/images/sattva_misc/not_found.jpg"
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const BlogsAndNews = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [search, setSearch] = useState("");

  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("Editor_token");
  const isAuthenticated = !!token && token.trim().length > 10;

  // Fetch Blogs
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://sattva-backend.demetrix.in/blogs");
      const data = await res.json();
      if (data.status === "success") {
        setBlogs(
          data.data.filter((b) => {
            const isDeleted =
              b.is_deleted === true ||
              b.is_deleted === "true" ||
              b.is_deleted === 1 ||
              b.deleted_at; // also catch non-null deleted_at

            return b.is_published === true && !isDeleted;
          })
        );

      } else {
        setBlogs([]);
        console.error(data.message || "Failed to fetch blogs");
      }
    } catch (err) {
      console.error(err);
      // alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Scroll functions
  const scroll = (direction) => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.75; // scroll ~75% of visible area

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Helpers (unchanged)
  const formatImage = (media = []) => {
    if (!Array.isArray(media)) return null;
    const image = media.find((m) => m.type === "image");
    if (!image?.url) return null;

    if (image.url.includes("drive.google.com")) {
      const match = image.url.match(/\/d\/([^/]+)/);
      if (match?.[1]) {
        return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
      }
    }
    return image.url;
  };

  // const fileToBase64 = (file) =>
  //   new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = reject;
  //   });

  const addMedia = () => {
    setEditingBlog((prev) => ({
      ...prev,
      media: prev.media
        ? [...prev.media, { type: "image", url: "", caption: "" }]
        : [{ type: "image", url: "", caption: "" }],
    }));
  };

  const handleMediaChange = (index, field, value) => {
    const updatedMedia = [...editingBlog.media];
    updatedMedia[index][field] = value;
    setEditingBlog((prev) => ({ ...prev, media: updatedMedia }));
  };

  const removeMedia = (index) => {
    const updatedMedia = editingBlog.media.filter((_, i) => i !== index);
    setEditingBlog((prev) => ({ ...prev, media: updatedMedia }));
  };

  // Delete Blog
  const handleDelete = async (slug) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    if (!token) {
      alert("You are not authenticated!");
      return;
    }
    try {
      const res = await fetch(`https://sattva-backend.demetrix.in/blogs/${slug}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      alert(
        data.message ||
        (data.status === "success" ? "Deleted successfully!" : "Delete failed")
      );
      if (data.status === "success") fetchBlogs();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // Add/Edit Blog
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      alert("You are not authenticated!");
      return;
    }
    try {
      const isEdit = blogs.some((b) => b.slug === editingBlog.slug);
      const url = isEdit
        ? `https://sattva-backend.demetrix.in/blogs/${editingBlog.slug}`
        : `https://sattva-backend.demetrix.in/blogs`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingBlog),
      });

      const data = await res.json();
      alert(
        data.message ||
        (data.status === "success"
          ? `${isEdit ? "Updated" : "Added"} successfully!`
          : "Operation failed")
      );

      if (data.status === "success") {
        setModalOpen(false);
        setEditingBlog(null);
        fetchBlogs();
      }
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="py-10 px-4">
        <div className="flex gap-6 overflow-x-auto">
          {[...Array(4)].map((_, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-[calc(50%-12px)] h-[420px] rounded-xl bg-white dark:bg-gray-800 shadow-md animate-pulse flex flex-col"
            >
              <div className="h-48 bg-gray-300 dark:bg-gray-700" />
              <div className="p-5 flex flex-col flex-grow gap-2">
                <div className="h-5 w-3/4 bg-gray-300 dark:bg-gray-700 rounded" />
                <div className="h-3 w-full bg-gray-300 dark:bg-gray-700 rounded mt-1" />
                <div className="h-3 w-5/6 bg-gray-300 dark:bg-gray-700 rounded mt-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }


  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title.toLowerCase().includes(search.toLowerCase()) ||
      blog.summary?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <section id="blog" className="">
      <div className="">
        <div className="">
          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            {
              blogs?.length > 0 &&
              <input
                type="text"
                placeholder="Search blogs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:max-w-md border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-700 dark:text-white"
              />}

            {isAuthenticated && (
              <button
                onClick={() => {
                  setEditingBlog({
                    slug: `new-${Date.now()}`,
                    title: "",
                    summary: "",
                    author: "",
                    content: "",
                    media: [],
                  });
                  setModalOpen(true);
                }}
                className="p-3 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition"
                title="Add Blog"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {
            blogs?.length > 0 ?


              <div className="relative">
                {/* LEFT ARROW */}
                <button
                  onClick={() => scroll("left")}
                  disabled={scrollRef.current?.scrollLeft <= 0}
                  style={{
                    position: "absolute",
                    left: "-24px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    color: "#374151",
                    width: "48px",
                    height: "48px",
                    borderRadius: "9999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "none",
                    cursor: scrollRef.current?.scrollLeft <= 0 ? "not-allowed" : "pointer",
                    opacity: scrollRef.current?.scrollLeft <= 0 ? 0.4 : 1,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (scrollRef.current?.scrollLeft > 0) {
                      e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(-50%)";
                  }}
                >
                  <ChevronLeftIcon style={{ width: "28px", height: "28px" }} />
                </button>



                {/* CAROUSEL CONTAINER */}
                <div
                  ref={scrollRef}
                  style={{
                    display: "flex",
                    gap: "24px",
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    scrollSnapType: "x mandatory",
                    paddingBottom: "12px",
                    msOverflowStyle: "none", /* IE and Edge */
                    scrollbarWidth: "none", /* Firefox */
                  }}
                  // Chrome / Safari hide scrollbar
                  className="[&::-webkit-scrollbar]:hidden"
                >
                  {filteredBlogs?.map((blog, idx) => {
                    const image = formatImage(blog.media);

                    return (
                      <div
                        key={idx}
                        className="snap-start flex-shrink-0 w-[calc(50%-12px)] group relative rounded-xl shadow-md overflow-hidden bg-white dark:bg-gray-800 h-[420px] flex flex-col"
                      >
                        {/* Image + Admin Icons */}
                        <div
                          className="relative cursor-pointer"
                          onClick={() => navigate(`/blogs/${blog.slug}`)}
                        >
                          {image ? (
                            <img
                              src={image}
                              alt={blog.title}
                              className="w-full h-48 object-cover"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                e.currentTarget.src = "/placeholder.jpg"; // optional fallback
                              }}
                            />
                          ) : (
                            <div className="h-48 bg-gray-200 flex items-center justify-center text-sm text-gray-500">
                              No image
                            </div>
                          )}

                          {isAuthenticated && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingBlog(blog);
                                  setModalOpen(true);
                                }}
                                className="p-1 rounded-full bg-blue-100 text-blue-700"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(blog.slug);
                                }}
                                className="p-1 rounded-full bg-red-100 text-red-700"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Content */}

                        <div className="p-5 flex flex-col flex-grow">
                          <h4 className="text-lg font-semibold mb-2 line-clamp-2">
                            {blog.title}
                          </h4>

                          <p className="text-slate-500 mt-2 line-clamp-3 leading-relaxed">
                            {blog.summary}
                          </p>

                          {/* Read More – fixed bottom-left */}
                          <div className="mt-auto pt-4">
                            <Link2
                              to={`/blogs/${blog.slug}`}
                              className="btn btn-link hover:text-blue-600 dark:hover:text-blue-600 after:bg-blue-600 dark:text-white transition duration-500"
                            >
                              Read More <i className="uil uil-arrow-right"></i>
                            </Link2>
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>

                {/* RIGHT ARROW */}
                <button
                  onClick={() => scroll("right")}
                  disabled={
                    scrollRef.current &&
                    scrollRef.current.scrollLeft + scrollRef.current.clientWidth >=
                    scrollRef.current.scrollWidth - 5
                  }
                  style={{
                    position: "absolute",
                    right: "-24px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    color: "#374151",
                    width: "48px",
                    height: "48px",
                    borderRadius: "9999px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "none",
                    cursor:
                      scrollRef.current &&
                        scrollRef.current.scrollLeft + scrollRef.current.clientWidth >=
                        scrollRef.current.scrollWidth - 5
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      scrollRef.current &&
                        scrollRef.current.scrollLeft + scrollRef.current.clientWidth >=
                        scrollRef.current.scrollWidth - 5
                        ? 0.4
                        : 1,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      scrollRef.current &&
                      scrollRef.current.scrollLeft + scrollRef.current.clientWidth <
                      scrollRef.current.scrollWidth - 5
                    ) {
                      e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(-50%)";
                  }}
                >
                  <ChevronRightIcon style={{ width: "28px", height: "28px" }} />
                </button>
              </div>
              :
              <div className="w-full flex items-center justify-center px-4 ">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-8 max-w-md w-full text-center"
                >
                  {/* Illustration */}
                  <motion.img
                    src={not_found}
                    alt="No blogs"
                    className="mx-auto h-40 mb-6"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />

                  {/* Text */}
                  <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                    No blogs found
                  </h3>

                  <p className="text-slate-500 dark:text-slate-400 mb-6">
                    We couldn’t find any blogs here. Once content is published, it’ll show up right away.
                  </p>

                  {/* CTA */}
                  <div className="flex justify-center gap-3">
                    <Link
                      to="/"
                      className="px-5 py-2.5 rounded-lg text-sm font-medium
              bg-slate-900 text-white dark:bg-white dark:text-slate-900
              hover:opacity-90 transition"
                    >
                      Go Home
                    </Link>
                  </div>
                </motion.div>
              </div>
          }
        </div>
      </div>

      {/* Modal for Add/Edit – unchanged */}
      {isAuthenticated && modalOpen && editingBlog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto mt-10">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              {blogs.some((b) => b.slug === editingBlog.slug) ? "Edit Blog" : "Add Blog"}
            </h3>

            <form onSubmit={handleFormSubmit} className="space-y-4 text-slate-600">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={editingBlog.title}
                onChange={(e) => setEditingBlog((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded-lg p-2"
                required
              />
              <input
                type="text"
                name="summary"
                placeholder="Summary"
                value={editingBlog.summary}
                onChange={(e) => setEditingBlog((prev) => ({ ...prev, summary: e.target.value }))}
                className="w-full border rounded-lg p-2"
              />
              <input
                type="text"
                name="author"
                placeholder="Author"
                value={editingBlog.author}
                onChange={(e) => setEditingBlog((prev) => ({ ...prev, author: e.target.value }))}
                className="w-full border rounded-lg p-2"
              />
              <textarea
                name="content"
                placeholder="Content"
                value={editingBlog.content}
                onChange={(e) => setEditingBlog((prev) => ({ ...prev, content: e.target.value }))}
                className="w-full border rounded-lg p-2 whitespace-pre-line"
                rows={6}
              />

              {/* Media */}
              <div>
                <h4 className="font-semibold mb-2 dark:text-white">Media (max 5)</h4>

                {editingBlog.media?.map((m, idx) => (
                  <div key={idx} className="border rounded-lg p-2 mb-2 relative">
                    <select
                      value={m.type}
                      onChange={(e) => handleMediaChange(idx, "type", e.target.value)}
                      className="border rounded-lg p-1 w-full mb-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>

                    {m.type === "image" ? (
                      <input
                        type="text"
                        placeholder="Image URL (Google Drive supported)"
                        value={m.url}
                        onChange={(e) => handleMediaChange(idx, "url", e.target.value)}
                        className="w-full border rounded-lg p-1 mb-1"
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder="Video URL (YouTube)"
                        value={m.url}
                        onChange={(e) => handleMediaChange(idx, "url", e.target.value)}
                        className="w-full border rounded-lg p-1 mb-1"
                      />
                    )}

                    <input
                      type="text"
                      placeholder="Caption"
                      value={m.caption}
                      onChange={(e) => handleMediaChange(idx, "caption", e.target.value)}
                      className="w-full border rounded-lg p-1"
                    />

                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="absolute top-1 right-1 text-red-600 font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {editingBlog.media?.length < 5 && (
                  <button
                    type="button"
                    onClick={addMedia}
                    className="bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 transition"
                  >
                    + Add Media
                  </button>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingBlog(null);
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </form>

            <button
              onClick={() => {
                setModalOpen(false);
                setEditingBlog(null);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 font-bold text-xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BlogsAndNews;